// @tennis-matches/services/trainerstunden/trainers
// Service for managing trainerstunden trainers

import { generateId } from "@tennis-matches/core/utils";
import { getSupabaseClient } from "../supabase/index.js";
import {
  buildTrainerAnonymizationUpdates,
  isTrainerEligibleForAnonymization,
} from "./trainer-lifecycle.js";
import { findCoordinatorFeeRecipientTrainer } from "./trainer-coordinator-fee.js";
import {
  POSTAL_ADDRESS_FIELD_KEYS,
  pickPostalAddressFields,
} from "../masterdata/postal-address.js";
import {
  upsertClubPersonMirrorFromTrainer,
  findTrainerIdForLinkedPlayerPersonId,
} from "../masterdata/trainer-club-person-sync.js";
import {
  findClubPeopleByNormalizedNameInTenant,
  normalizeClubPersonNamePart,
} from "../masterdata/club-person-name-match.js";
import {
  buildFreeTrainingTrainerSeed,
  trainersIncludeFreeTraining,
} from "./free-training-trainer.js";
import {
  buildTrainerClubMembershipsRpcPayload,
  replaceTrainerClubMembershipsRemote,
} from "./trainer-club-memberships.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function isMissingTableError(error) {
  const code = String(error?.code || "");
  const msg = String(error?.message || error?.details || "");
  return (
    code === "42P01" ||
    /does not exist|relation .*trainerstunden_trainers/i.test(msg)
  );
}

/**
 * PostgREST schema-cache: Spalte in DB noch nicht migriert.
 * @param {unknown} error
 */
function extractMissingColumnFromError(error) {
  const msg = String(error?.message || error?.details || "");
  const code = String(error?.code || "");
  if (code === "PGRST204") {
    const m204 = /Could not find the '([^']+)' column/i.exec(msg);
    if (m204) return String(m204[1]).trim();
  }
  const m =
    /Could not find the '([^']+)' column of 'trainerstunden_trainers'/i.exec(
      msg,
    );
  if (m) return String(m[1]).trim();
  const mPg =
    /column trainerstunden_trainers\.([a-z0-9_]+) does not exist/i.exec(msg);
  return mPg ? String(mPg[1]).trim() : "";
}

/**
 * @param {Record<string, unknown>} payload
 * @param {string} column
 */
function omitTrainerPayloadColumn(payload, column) {
  if (!column || !(column in payload)) return payload;
  const next = { ...payload };
  delete next[column];
  return next;
}

const TRAINER_SCHEMA_RETRY_MAX = 12;

/**
 * @param {Record<string, unknown>} payload
 * @param {(p: Record<string, unknown>) => Promise<{ data: unknown, error: unknown }>} run
 */
async function persistTrainerWithSchemaRetry(payload, run) {
  let current = { ...payload };
  /** @type {string[]} */
  const schemaOmittedColumns = [];
  for (let attempt = 0; attempt < TRAINER_SCHEMA_RETRY_MAX; attempt += 1) {
    const { data, error } = await run(current);
    if (!error) return { data, error: null, schemaOmittedColumns };
    const missingCol = extractMissingColumnFromError(error);
    if (!missingCol) return { data, error, schemaOmittedColumns };
    schemaOmittedColumns.push(missingCol);
    current = omitTrainerPayloadColumn(current, missingCol);
  }
  return {
    data: null,
    error: {
      code: "trainer-schema-retry-exhausted",
      message: "trainer-schema-retry-exhausted",
    },
    schemaOmittedColumns,
  };
}

/**
 * @param {string[]} omitted
 */
function buildTrainerPersistMeta(omitted) {
  const schemaOmittedColumns = Array.isArray(omitted) ? omitted : [];
  const postalSchemaPending = schemaOmittedColumns.some((col) =>
    POSTAL_ADDRESS_FIELD_KEYS.includes(col),
  );
  const paymentDueDaysSchemaPending =
    schemaOmittedColumns.includes("payment_due_days");
  const invoiceVatSchemaPending =
    schemaOmittedColumns.includes("invoice_vat_mode") ||
    schemaOmittedColumns.includes("invoice_vat_rate");
  return {
    schemaOmittedColumns,
    postalSchemaPending,
    paymentDueDaysSchemaPending,
    invoiceVatSchemaPending,
  };
}

function readFallback() {
  try {
    const raw = localStorage.getItem(TRAINERS_FALLBACK_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (_) {
    return [];
  }
}

function writeFallback(list) {
  try {
    localStorage.setItem(
      TRAINERS_FALLBACK_KEY,
      JSON.stringify(Array.isArray(list) ? list : []),
    );
  } catch (_) {
    // ignore
  }
}

const LOAD_TRAINERS_CACHE_MS = 15000;
/** @type {Promise<{ ok: boolean, data?: object[], error?: unknown }> | null} */
let __loadTrainersInflight = null;
/** @type {{ ts: number, result: { ok: boolean, data?: object[], error?: unknown } } | null} */
let __loadTrainersCache = null;

export function invalidateTrainersListCache() {
  __loadTrainersCache = null;
  __loadTrainersInflight = null;
  __trainerByEmailCache = null;
  __trainerByEmailInflight = null;
  __trainerByEmailInflightKey = "";
}

/**
 * System-Trainer „Freies Training“ nur anlegen, wenn der Nutzer Stammdaten pflegen darf
 * (Koordinator / Planner), nicht reiner Trainer-Login.
 *
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 */
async function __shouldEnsureFreeTrainingTrainer(supabase) {
  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData?.user?.id) return false;
  const email = String(authData.user.email || "")
    .trim()
    .toLowerCase();
  if (!email) return true;
  const { data: trRows, error: trErr } = await supabase
    .from("trainerstunden_trainers")
    .select("id, login_role, created_by")
    .ilike("login_email", email)
    .limit(1);
  const tr = Array.isArray(trRows) ? trRows[0] : null;
  if (trErr || !tr?.id) return true;
  const role = String(tr.login_role || "trainer").trim();
  if (role === "planner") return true;
  return false;
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @param {string} authUserId
 * @param {string} authEmail
 */
function __canUpdateTrainerRow(trainer, authUserId, authEmail) {
  const uid = String(authUserId || "").trim();
  const email = String(authEmail || "")
    .trim()
    .toLowerCase();
  const owner = String(trainer?.created_by || "").trim();
  const loginEmail = String(trainer?.login_email || "")
    .trim()
    .toLowerCase();
  if (uid && owner && uid === owner) return true;
  if (email && loginEmail && email === loginEmail) return true;
  return false;
}

async function __loadTrainersImpl() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_trainers")
      .select("*")
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true });
    if (error) {
      if (isMissingTableError(error)) {
        return { ok: true, data: readFallback() };
      }
      return { ok: false, error };
    }
    const list = data || [];
    if (
      !trainersIncludeFreeTraining(list) &&
      (await __shouldEnsureFreeTrainingTrainer(supabase))
    ) {
      const seed = buildFreeTrainingTrainerSeed();
      const created = await createTrainer(seed);
      if (created?.ok && created.data) {
        return { ok: true, data: [...list, created.data] };
      }
    }
    return { ok: true, data: list };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function loadTrainers(options = {}) {
  const force = Boolean(options?.force);
  const now = Date.now();

  if (
    !force &&
    __loadTrainersCache &&
    now - __loadTrainersCache.ts < LOAD_TRAINERS_CACHE_MS
  ) {
    return __loadTrainersCache.result;
  }

  if (!force && __loadTrainersInflight) {
    return __loadTrainersInflight;
  }

  const promise = __loadTrainersImpl()
    .then((result) => {
      __loadTrainersCache = { ts: Date.now(), result };
      return result;
    })
    .finally(() => {
      if (__loadTrainersInflight === promise) {
        __loadTrainersInflight = null;
      }
    });

  __loadTrainersInflight = promise;
  return promise;
}

const TRAINER_BILLING_ISSUER_SELECT =
  "id,first_name,last_name,login_email,login_role,coordination_periods,address,street,house_number,postal_code,city,country,invoice_vat_mode,invoice_vat_rate,payment_due_days,tenant_id";

/** Stammdaten für Rechnungssteller-Vorschau, wenn der Koordinator nicht im lokalen Cache liegt. */
export async function loadTrainerBillingIssuerProfileRemote(trainerId) {
  const id = String(trainerId || "").trim();
  if (!id) return { ok: false, error: "missing-id" };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_trainers")
      .select(TRAINER_BILLING_ISSUER_SELECT)
      .eq("id", id)
      .maybeSingle();
    if (error) return { ok: false, error };
    return { ok: true, data: data || null };
  } catch (error) {
    return { ok: false, error };
  }
}

const TRAINER_BY_EMAIL_CACHE_MS = 15000;
let __trainerByEmailInflight = null;
let __trainerByEmailInflightKey = "";
let __trainerByEmailCache = null;

export function invalidateTrainerByLoginEmailCache() {
  __trainerByEmailCache = null;
  __trainerByEmailInflight = null;
  __trainerByEmailInflightKey = "";
}

export async function loadTrainerByLoginEmail(loginEmail) {
  const email = String(loginEmail || "")
    .trim()
    .toLowerCase();
  if (!email) return { ok: true, data: null };

  if (__trainerByEmailInflight && __trainerByEmailInflightKey === email) {
    return __trainerByEmailInflight;
  }

  const now = Date.now();
  if (
    __trainerByEmailCache &&
    __trainerByEmailCache.key === email &&
    now - __trainerByEmailCache.ts < TRAINER_BY_EMAIL_CACHE_MS
  ) {
    return __trainerByEmailCache.result;
  }

  __trainerByEmailInflightKey = email;
  __trainerByEmailInflight = __loadTrainerByLoginEmailImpl(email)
    .then((result) => {
      __trainerByEmailCache = { key: email, ts: Date.now(), result };
      return result;
    })
    .finally(() => {
      if (__trainerByEmailInflightKey === email) {
        __trainerByEmailInflight = null;
        __trainerByEmailInflightKey = "";
      }
    });

  return __trainerByEmailInflight;
}

async function __loadTrainerByLoginEmailImpl(email) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data: rpcData, error: rpcError } = await supabase.rpc(
      "trainerstunden_get_trainer_by_login_email",
      {
        p_email: email,
      },
    );
    if (
      !rpcError &&
      rpcData &&
      typeof rpcData === "object" &&
      rpcData.ok !== false
    ) {
      return { ok: true, data: rpcData.trainer || null };
    }

    const rpcMissing =
      rpcError &&
      (String(rpcError?.code || "") === "PGRST202" ||
        String(rpcError?.code || "") === "42883");
    if (rpcError && !rpcMissing) {
      return { ok: false, error: rpcError };
    }

    const { data, error } = await supabase
      .from("trainerstunden_trainers")
      .select("*")
      .ilike("login_email", email)
      .limit(1)
      .maybeSingle();

    if (error) {
      if (isMissingTableError(error)) {
        const list = readFallback();
        const { authEmailsEquivalent } =
          await import("./normalize-email-for-auth-match.js");
        const found =
          list.find((x) => authEmailsEquivalent(x?.login_email, email)) ||
          list.find(
            (x) =>
              String(x?.login_email || "")
                .trim()
                .toLowerCase() === email,
          ) ||
          null;
        return { ok: true, data: found };
      }
      return { ok: false, error };
    }
    return { ok: true, data: data || null };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function createTrainer(trainer) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const trainerId = String(trainer?.id || "").trim();
    const tenantId = String(trainer?.tenant_id || "").trim();
    const firstName = trainer?.first_name;
    const lastName = trainer?.last_name;
    const loginEmail = trainer?.login_email;

    let linkedPlayerId = __asStr(trainer?.linked_player_person_id);
    if (
      !linkedPlayerId &&
      tenantId &&
      normalizeClubPersonNamePart(firstName) &&
      normalizeClubPersonNamePart(lastName)
    ) {
      const matches = await findClubPeopleByNormalizedNameInTenant(
        supabase,
        tenantId,
        firstName,
        lastName,
        { excludeIds: [trainerId] },
      );
      const native = matches.filter(
        (row) => !String(row?.contact_source || "").trim(),
      );
      if (native.length === 1) {
        linkedPlayerId = String(native[0]?.id || "").trim();
      }
    }

    if (linkedPlayerId) {
      const existingTrainerId = await findTrainerIdForLinkedPlayerPersonId(
        supabase,
        linkedPlayerId,
      );
      if (existingTrainerId) {
        return {
          ok: false,
          error: "trainer-already-exists-for-person",
          reason: "trainer-already-exists-for-person",
          existingTrainerId,
        };
      }
    }

    const { data, error, schemaOmittedColumns } =
      await persistTrainerWithSchemaRetry(trainer, (payload) =>
        supabase
          .from("trainerstunden_trainers")
          .insert([payload])
          .select()
          .single(),
      );
    const persistMeta = buildTrainerPersistMeta(schemaOmittedColumns);
    if (error) {
      if (isMissingTableError(error)) {
        const list = readFallback();
        const next = [...list, trainer];
        writeFallback(next);
        invalidateTrainersListCache();
        return { ok: true, data: trainer, ...persistMeta };
      }
      return { ok: false, error, ...persistMeta };
    }
    if (data) {
      const mirrorRow = linkedPlayerId
        ? { ...data, linked_player_person_id: linkedPlayerId }
        : data;
      await upsertClubPersonMirrorFromTrainer(mirrorRow);
    }
    invalidateTrainersListCache();
    return {
      ok: true,
      data,
      linkedPlayerPersonId: linkedPlayerId || null,
      ...persistMeta,
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Legt einen Trainer-Datensatz für einen bestehenden Spieler-Kontakt an (z. B. Trainerassistent).
 *
 * @param {object} playerPerson club_people-Zeile (nativer Spieler, contact_source null)
 * @param {{ isAssistantOnly?: boolean, staffRole?: string, tenantId?: string }} [options]
 */
export async function createTrainerFromClubPerson(playerPerson, options = {}) {
  const person =
    playerPerson && typeof playerPerson === "object" ? playerPerson : {};
  const pid = __asStr(person.id);
  if (!pid) return { ok: false, reason: "missing-person-id" };
  if (__asStr(person.contact_source)) {
    return { ok: false, reason: "not-native-player" };
  }

  const tenantId = __asStr(options.tenantId) || __asStr(person.tenant_id);
  if (!tenantId) return { ok: false, reason: "missing-tenant" };

  const staffRole =
    __asStr(options.staffRole) ||
    (options.isAssistantOnly === false ? "trainer" : "assistant");
  const rolePatch =
    staffRole === "coordinator"
      ? { is_assistant_only: false, login_role: "planner" }
      : staffRole === "trainer"
        ? { is_assistant_only: false, login_role: "trainer" }
        : { is_assistant_only: true, login_role: "trainer" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, error: "no-supabase" };

  const existingTrainerId = await findTrainerIdForLinkedPlayerPersonId(
    supabase,
    pid,
  );
  if (existingTrainerId) {
    return {
      ok: false,
      reason: "trainer-already-exists-for-person",
      existingTrainerId,
    };
  }

  const seed = {
    id: generateId(12),
    tenant_id: tenantId,
    first_name: person.first_name ?? null,
    last_name: person.last_name ?? null,
    login_email: __asStr(person.email) || null,
    linked_player_person_id: pid,
    notes: person.notes ?? null,
    availability: [],
    absences: [],
    standard_rate_periods: [],
    honorar_rate_periods: [],
    parent_billable_rate_periods: [],
    club_mediation_fee_enabled: false,
    club_mediation_fee_billing_contact_id: null,
    hourly_rates: [],
    payment_due_days: 7,
    is_inactive: false,
    is_free_training: false,
    ...pickPostalAddressFields(person),
    ...rolePatch,
  };

  const created = await createTrainer(seed);
  if (!created.ok) return created;

  const trainerId = __asStr(created.data?.id);
  if (trainerId) {
    const clubPayload = buildTrainerClubMembershipsRpcPayload(
      [tenantId],
      tenantId,
    );
    const clubRes = await replaceTrainerClubMembershipsRemote(
      trainerId,
      clubPayload,
    );
    if (!clubRes.ok) {
      return {
        ...created,
        clubMembershipWarning: clubRes.reason || "club-membership-failed",
      };
    }
  }

  return created;
}

function __splitFullName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

/**
 * Legt bei Self-Service-Registrierung (Intent trainer/both) einen Trainer-Stub an.
 *
 * @param {{ email?: string, userId?: string, firstName?: string, lastName?: string, fullName?: string, tenantId?: string }} ctx
 */
export async function ensureSelfSignupTrainerRecord(ctx = {}) {
  const email = String(ctx.email || "")
    .trim()
    .toLowerCase();
  const tenantId = String(ctx.tenantId || "").trim();
  if (!email) return { ok: false, reason: "missing-email" };
  if (!tenantId) return { ok: false, reason: "missing-tenant" };

  const existingRes = await loadTrainerByLoginEmail(email);
  if (existingRes?.ok && existingRes.data?.id) {
    return { ok: true, trainer: existingRes.data, created: false };
  }

  let firstName = String(ctx.firstName || "").trim();
  let lastName = String(ctx.lastName || "").trim();
  if (!firstName && !lastName) {
    const split = __splitFullName(ctx.fullName);
    firstName = split.firstName;
    lastName = split.lastName;
  }
  const localPart = email.split("@")[0] || "";
  const seed = {
    id: generateId(12),
    tenant_id: tenantId,
    first_name: firstName || "Trainer",
    last_name: lastName || localPart || "Profil",
    login_email: email,
    login_role: "trainer",
    is_assistant_only: false,
    is_inactive: false,
    is_free_training: false,
    availability: [],
    absences: [],
    standard_rate_periods: [],
    honorar_rate_periods: [],
    parent_billable_rate_periods: [],
    club_mediation_fee_enabled: false,
    club_mediation_fee_billing_contact_id: null,
    hourly_rates: [],
    payment_due_days: 7,
    parent_booking_manual_approval: true,
    ui_permissions: {
      tabs: {
        hub: true,
        planning: true,
        agenda: true,
        rechnungswesen: true,
        zahlungen: true,
        ausgabenerfassung: true,
        bilanzierung: true,
        masterdata: true,
      },
    },
  };

  const created = await createTrainer(seed);
  if (!created.ok) {
    return { ok: false, reason: "create-failed", error: created.error };
  }
  invalidateTrainerByLoginEmailCache();
  return { ok: true, trainer: created.data, created: true };
}

/**
 * Trainer-Datensatz für Koordinator-Honorar-Auszahlung (login_email = Auth-E-Mail).
 * Legt bei Bedarf einen schlanken Datensatz an (nur intern, nicht für Spieler sichtbar).
 *
 * @param {{ email?: string, userId?: string, firstName?: string, lastName?: string, tenantId?: string }} ctx
 */
export async function ensureCoordinatorFeeRecipientTrainerRecord(ctx = {}) {
  let email = String(ctx.email || "")
    .trim()
    .toLowerCase();
  let userId = String(ctx.userId || "").trim();
  let firstName = String(ctx.firstName || "").trim();
  let lastName = String(ctx.lastName || "").trim();

  const supabase = await getSupabaseClient();
  if (supabase) {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (user) {
      if (!email)
        email = String(user.email || "")
          .trim()
          .toLowerCase();
      if (!userId) userId = String(user.id || "").trim();
      const meta =
        user.user_metadata && typeof user.user_metadata === "object"
          ? user.user_metadata
          : {};
      if (!firstName)
        firstName = String(meta.first_name || meta.given_name || "").trim();
      if (!lastName)
        lastName = String(meta.last_name || meta.family_name || "").trim();
    }
  }

  if (!email && !userId) {
    return { ok: false, code: "auth-missing" };
  }

  if (email) {
    const byLogin = await loadTrainerByLoginEmail(email);
    if (byLogin?.ok && byLogin.data?.id) {
      return { ok: true, data: byLogin.data, created: false };
    }
  }

  const loadResult = await loadTrainers({ force: true });
  const trainers = loadResult.ok ? loadResult.data || [] : [];
  const existing = findCoordinatorFeeRecipientTrainer(trainers, {
    email,
    userId,
  });
  if (existing?.id) {
    return {
      ok: true,
      data: __withCoordinatorLoginEmail(existing, email),
      created: false,
    };
  }

  const tenantId =
    String(ctx.tenantId || "").trim() ||
    trainers.map((tr) => String(tr?.tenant_id || "").trim()).find(Boolean) ||
    "";

  const byName = __findTrainerByNormalizedName(
    trainers,
    firstName,
    lastName,
    tenantId,
  );
  if (byName?.id) {
    return {
      ok: true,
      data: await __reuseCoordinatorTrainerRow(byName, email, userId),
      created: false,
    };
  }

  if (!email) {
    return { ok: false, code: "coordinator-recipient-missing" };
  }

  if (!tenantId) {
    return { ok: false, code: "tenant-missing" };
  }

  const localPart = email.split("@")[0] || "";
  const seed = {
    id: generateId(12),
    tenant_id: tenantId,
    first_name: firstName || "Trainingskoordinator",
    last_name: lastName || localPart || "Koordinator",
    login_email: email,
    login_role: "planner",
    is_assistant_only: false,
    is_inactive: false,
    is_free_training: false,
    notes: "Automatisch für Koordinator-Honorar-Auszahlung angelegt.",
    availability: [],
    absences: [],
    standard_rate_periods: [],
    honorar_rate_periods: [],
    parent_billable_rate_periods: [],
    club_mediation_fee_enabled: false,
    club_mediation_fee_billing_contact_id: null,
    hourly_rates: {},
    payment_due_days: 7,
    default_coordinator_fee_type: "none",
    default_coordinator_fee_value: null,
  };

  const created = await createTrainer(seed);
  if (created.ok && created.data?.id) {
    return { ok: true, data: created.data, created: true };
  }

  // Name kollidiert oft mit bestehendem Trainer-Profil (z. B. Vereinsadmin = Trainer).
  const existingId = String(created.existingTrainerId || "").trim();
  if (existingId) {
    const refreshed = await loadTrainers({ force: true });
    const list = refreshed.ok ? refreshed.data || [] : trainers;
    const row =
      list.find((tr) => String(tr?.id || "").trim() === existingId) || null;
    if (row?.id) {
      return {
        ok: true,
        data: await __reuseCoordinatorTrainerRow(row, email, userId),
        created: false,
      };
    }
    // RLS kann die Zeile ausblenden – für die Session-Persistenz reicht die ID.
    return {
      ok: true,
      data: __withCoordinatorLoginEmail(
        {
          id: existingId,
          tenant_id: tenantId,
          first_name: firstName,
          last_name: lastName,
        },
        email,
      ),
      created: false,
    };
  }

  const retryLoad = await loadTrainers({ force: true });
  const fallbackName = __findTrainerByNormalizedName(
    retryLoad.ok ? retryLoad.data || trainers : trainers,
    firstName,
    lastName,
    tenantId,
  );
  if (fallbackName?.id) {
    return {
      ok: true,
      data: await __reuseCoordinatorTrainerRow(fallbackName, email, userId),
      created: false,
    };
  }

  return {
    ok: false,
    code: "create-failed",
    error: created.error || created.reason,
  };
}

/**
 * @param {object} trainer
 * @param {string} email
 */
function __withCoordinatorLoginEmail(trainer, email) {
  const row = trainer && typeof trainer === "object" ? { ...trainer } : {};
  const mail = String(email || "")
    .trim()
    .toLowerCase();
  if (mail && !String(row.login_email || "").trim()) {
    row.login_email = mail;
  }
  return row;
}

/**
 * @param {object[]} trainers
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} tenantId
 */
function __findTrainerByNormalizedName(
  trainers,
  firstName,
  lastName,
  tenantId,
) {
  const fn = normalizeClubPersonNamePart(firstName);
  const ln = normalizeClubPersonNamePart(lastName);
  if (!fn || !ln) return null;
  const tid = String(tenantId || "").trim();
  const list = Array.isArray(trainers) ? trainers : [];
  const matches = list.filter((tr) => {
    if (tr?.is_free_training) return false;
    if (tid && String(tr?.tenant_id || "").trim() !== tid) return false;
    return (
      normalizeClubPersonNamePart(tr?.first_name) === fn &&
      normalizeClubPersonNamePart(tr?.last_name) === ln
    );
  });
  return matches.length === 1 ? matches[0] : null;
}

/**
 * Bestehenden Trainer als Koordinator-Empfänger nutzen.
 * login_email nur patchen, wenn RLS das erlaubt – sonst nur im Speicher setzen.
 *
 * @param {object} row
 * @param {string} email
 * @param {string} userId
 */
async function __reuseCoordinatorTrainerRow(row, email, userId) {
  const mail = String(email || "")
    .trim()
    .toLowerCase();
  const currentLogin = String(row?.login_email || "")
    .trim()
    .toLowerCase();
  if (mail && !currentLogin && __canUpdateTrainerRow(row, userId, mail)) {
    const patched = await updateTrainer(row.id, { login_email: mail });
    if (patched?.ok && patched.data?.id) {
      return patched.data;
    }
  }
  return __withCoordinatorLoginEmail(row, mail);
}

export async function updateTrainer(id, updates) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error, schemaOmittedColumns } =
      await persistTrainerWithSchemaRetry(updates, (payload) =>
        supabase
          .from("trainerstunden_trainers")
          .update(payload)
          .eq("id", id)
          .select()
          .single(),
      );
    const persistMeta = buildTrainerPersistMeta(schemaOmittedColumns);
    if (error) {
      if (isMissingTableError(error)) {
        const list = readFallback();
        const next = list.map((x) => (x.id === id ? { ...x, ...updates } : x));
        const out = next.find((x) => x.id === id) || { id, ...updates };
        writeFallback(next);
        invalidateTrainersListCache();
        return { ok: true, data: out, ...persistMeta };
      }
      return { ok: false, error, ...persistMeta };
    }
    if (data) await upsertClubPersonMirrorFromTrainer(data);
    invalidateTrainersListCache();
    return { ok: true, data, ...persistMeta };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/** @deprecated Trainer werden nicht gelöscht – {@link deactivateTrainer} verwenden. */
export async function deleteTrainer(_id) {
  return {
    ok: false,
    error: {
      code: "trainer-delete-forbidden",
      message: "trainer-delete-forbidden",
    },
  };
}

export async function deactivateTrainer(id) {
  return updateTrainer(id, {
    is_inactive: true,
    inactive_at: new Date().toISOString(),
  });
}

export async function reactivateTrainer(id) {
  return updateTrainer(id, {
    is_inactive: false,
    inactive_at: null,
  });
}

/**
 * @param {string} id
 */
export async function anonymizeTrainer(id) {
  return updateTrainer(id, buildTrainerAnonymizationUpdates(id));
}

/**
 * Wendet Anonymisierung auf alle fälligen Trainer an (Client nach loadTrainers).
 * @param {Array<Record<string, unknown>>} trainers
 */
export async function applyEligibleTrainerAnonymizations(trainers) {
  const list = Array.isArray(trainers) ? trainers : [];
  let next = list;
  const supabase = await getSupabaseClient();
  let authUserId = "";
  let authEmail = "";
  if (supabase) {
    const { data: authData } = await supabase.auth.getUser();
    authUserId = String(authData?.user?.id || "").trim();
    authEmail = String(authData?.user?.email || "")
      .trim()
      .toLowerCase();
  }
  for (const tr of list) {
    if (!isTrainerEligibleForAnonymization(tr)) continue;
    const tid = String(tr?.id || "").trim();
    if (!tid) continue;
    if (authUserId || authEmail) {
      if (!__canUpdateTrainerRow(tr, authUserId, authEmail)) continue;
    }
    const r = await anonymizeTrainer(tid);
    if (r.ok && r.data) {
      next = next.map((x) => (String(x?.id) === tid ? r.data : x));
    }
  }
  return { ok: true, data: next };
}
